import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ResPillStatus } from '../res-pill/res-pill.component';
import { resPillIcon } from '../../shared/test-run-status-icon';

/**
 * Ergebnis eines Schritts als Punkt in der Schrittliste
 * (`page-testlauf.jsx:93-104`). Ein noch nicht bewerteter Schritt bekommt einen
 * leeren, gestrichelten Ring; jedes Ergebnis einen gefuellten Punkt mit Icon.
 *
 * <p>Neben {@link ResPillComponent}, nicht statt ihr: die Pille traegt den Status
 * als Text und steht im Detail, der Punkt steht in der Liste, wo nur 18 Pixel
 * Platz sind. Beide teilen sich Statuswerte und Farbtokens, damit dieselbe Sache
 * an beiden Stellen gleich aussieht.
 */
@Component({
  selector: 'app-step-dot',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      class="step-dot"
      [class.step-dot--active]="active()"
      [attr.data-status]="status()"
      role="img"
      [attr.aria-label]="label()"
      data-testid="step-dot"
    >
      @if (icon(); as iconName) {
        <span class="step-dot__icon material-icons" aria-hidden="true">{{ iconName }}</span>
      }
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
    }

    .step-dot {
      width: 1.125rem;
      height: 1.125rem;
      border-radius: 50%;
      flex-shrink: 0;
      display: grid;
      place-items: center;
      background: var(--to-slate-dot);

      &[data-status='not-run'] {
        background: transparent;
        border: 2px dashed var(--to-slate-dot-soft);
      }
      &[data-status='passed'] {
        background: var(--to-green-dot);
      }
      &[data-status='failed'] {
        background: var(--to-chart-failed);
      }
      &[data-status='blocked'] {
        background: var(--to-amber-deep);
      }
      &[data-status='skipped'] {
        background: var(--to-slate-dot-soft);
      }
      &[data-status='running'] {
        background: var(--to-blue-dot);
      }
    }

    .step-dot--active[data-status='not-run'] {
      border-color: var(--to-brand);
    }

    .step-dot__icon {
      font-size: 0.6875rem;
      width: 0.6875rem;
      height: 0.6875rem;
      line-height: 0.6875rem;
      color: var(--to-on-brand);
    }
  `,
})
export class StepDotComponent {
  readonly status = input.required<ResPillStatus>();

  /** Uebersetzter Statustext — der Punkt darf den Status nicht allein ueber Farbe tragen. */
  readonly label = input.required<string>();

  readonly active = input<boolean>(false);

  readonly icon = computed(() => (this.status() === 'not-run' ? null : resPillIcon(this.status())));
}
